import { useState } from 'react';
import LegalLayout, {
  useLegalCompanyInfo, Section, InfoTable, AlertBox, Highlight, CookieTable
} from './LegalLayout';

const SECTIONS = [
  { id: 'que-son', label: '1. ¿Qué son las cookies?' },
  { id: 'tipos', label: '2. Tipos de cookies' },
  { id: 'cookies-usadas', label: '3. Cookies que usamos' },
  { id: 'terceros', label: '4. Cookies de terceros' },
  { id: 'gestion', label: '5. Gestión de cookies' },
  { id: 'preferencias', label: '6. Centro de preferencias' },
  { id: 'cambios', label: '7. Cambios a esta política' },
  { id: 'contacto', label: '8. Contacto' },
];

const ESSENTIAL_COOKIES = [
  { name: 'okr_session', provider: 'OkReserva', purpose: 'Mantiene la sesión de usuario activa y segura', duration: 'Sesión', type: 'Esencial' },
  { name: 'okr_auth_token', provider: 'OkReserva', purpose: 'Token de autenticación JWT para acceso autenticado', duration: '7 días', type: 'Esencial' },
  { name: 'okr_client_session', provider: 'OkReserva', purpose: 'Sesión del portal del cliente (OUser)', duration: '24 horas', type: 'Esencial' },
  { name: 'okr_restaurant_id', provider: 'OkReserva', purpose: 'Identifica el restaurante activo en la sesión', duration: 'Sesión', type: 'Esencial' },
  { name: 'XSRF-TOKEN', provider: 'OkReserva', purpose: 'Protección contra ataques CSRF', duration: 'Sesión', type: 'Esencial' },
  { name: 'okr_consent', provider: 'OkReserva', purpose: 'Registra las preferencias de consentimiento de cookies', duration: '365 días', type: 'Esencial' },
];

const ANALYTICS_COOKIES = [
  { name: '_ga', provider: 'Google Analytics', purpose: 'Distingue usuarios únicos y calcula estadísticas de visitas', duration: '2 años', type: 'Analítica' },
  { name: '_ga_*', provider: 'Google Analytics', purpose: 'Mantiene el estado de sesión de Google Analytics', duration: '2 años', type: 'Analítica' },
  { name: '_gid', provider: 'Google Analytics', purpose: 'Distingue usuarios para estadísticas del día', duration: '24 horas', type: 'Analítica' },
  { name: 'okr_analytics', provider: 'OkReserva', purpose: 'Analítica interna: páginas visitadas, tiempo en sitio, flujo de navegación', duration: '30 días', type: 'Analítica' },
];

const PREF_COOKIES = [
  { name: 'okr_theme', provider: 'OkReserva', purpose: 'Recuerda la preferencia de tema claro/oscuro del usuario', duration: '1 año', type: 'Preferencia' },
  { name: 'okr_lang', provider: 'OkReserva', purpose: 'Idioma preferido de la interfaz', duration: '1 año', type: 'Preferencia' },
  { name: 'okr_sidebar', provider: 'OkReserva', purpose: 'Estado expandido/colapsado del menú lateral', duration: '90 días', type: 'Preferencia' },
  { name: 'okr_last_restaurant', provider: 'OkReserva', purpose: 'Último restaurante seleccionado (multi-ubicación)', duration: '30 días', type: 'Preferencia' },
];

const MARKETING_COOKIES = [
  { name: '_fbp', provider: 'Facebook/Meta', purpose: 'Pixel de Meta para medir conversiones de anuncios', duration: '90 días', type: 'Marketing' },
  { name: 'fr', provider: 'Facebook/Meta', purpose: 'Publicidad y análisis de Meta', duration: '90 días', type: 'Marketing' },
  { name: '_gcl_au', provider: 'Google Ads', purpose: 'Seguimiento de conversiones de Google Ads', duration: '90 días', type: 'Marketing' },
  { name: 'okr_promo', provider: 'OkReserva', purpose: 'Seguimiento de campañas promocionales de OkReserva', duration: '30 días', type: 'Marketing' },
];

export default function CookiesPage() {
  const co = useLegalCompanyInfo();
  const [expanded, setExpanded] = useState({ essential: true, analytics: false, preference: false, marketing: false });

  const toggle = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  return (
    <LegalLayout
      title="Política de Cookies"
      subtitle="Información sobre las cookies y tecnologías de rastreo que utilizamos en OkReserva y cómo puedes gestionarlas."
      sections={SECTIONS}
      lastUpdated={co.last_updated}
    >
      <AlertBox type="orange">
        OkReserva utiliza cookies propias y de terceros para mejorar tu experiencia. Al continuar utilizando nuestra plataforma, aceptas el uso de cookies esenciales. Para las cookies no esenciales, solicitamos tu consentimiento explícito.
      </AlertBox>

      <Section id="que-son" title="1. ¿Qué son las Cookies?">
        <p>Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo (ordenador, teléfono, tablet) cuando visitas un sitio web. Tienen diversas funciones, como recordar tus preferencias, mantener tu sesión activa o ayudarnos a entender cómo utilizas nuestra plataforma.</p>
        <p>Además de las cookies, OkReserva puede utilizar otras tecnologías similares:</p>
        <InfoTable rows={[
          ['Cookies', 'Archivos de texto almacenados en el navegador con fecha de expiración.'],
          ['Local Storage', 'Almacenamiento en el navegador sin fecha de expiración automática (útil para preferencias de la app).'],
          ['Session Storage', 'Almacenamiento temporal que se elimina al cerrar el navegador.'],
          ['Píxeles de seguimiento', 'Imágenes de 1x1 px invisibles usadas para rastrear comportamiento (solo con consentimiento).'],
          ['SDK de terceros', 'Librerías de JavaScript de proveedores externos (Google Analytics, Meta Pixel, etc.).'],
        ]} />
      </Section>

      <Section id="tipos" title="2. Tipos de Cookies que Utilizamos">
        <p>Clasificamos nuestras cookies según su <Highlight>propósito</Highlight> y su <Highlight>duración</Highlight>:</p>

        <h3 className="text-base font-bold text-gray-900 mt-4 mb-2">Por propósito:</h3>
        <InfoTable rows={[
          ['🔒 Esenciales', 'Necesarias para el funcionamiento básico de la plataforma. Sin ellas, no puedes acceder a tu cuenta ni usar los servicios. No pueden desactivarse.'],
          ['📊 Analíticas', 'Nos ayudan a entender cómo usas la plataforma para mejorarla. No recopilan datos personales identificables.'],
          ['⚙️ De preferencia', 'Recuerdan tus configuraciones y preferencias personales para ofrecerte una experiencia personalizada.'],
          ['📣 De marketing', 'Usadas para mostrarte anuncios relevantes en OkReserva y plataformas externas. Solo con tu consentimiento.'],
        ]} />

        <h3 className="text-base font-bold text-gray-900 mt-4 mb-2">Por duración:</h3>
        <InfoTable rows={[
          ['De sesión', 'Se eliminan automáticamente cuando cierras el navegador.'],
          ['Persistentes', 'Permanecen almacenadas hasta su fecha de expiración o hasta que las elimines manualmente.'],
        ]} />
      </Section>

      <Section id="cookies-usadas" title="3. Cookies que Utilizamos">
        <p>A continuación encontrarás el listado completo de cookies utilizadas en OkReserva, agrupadas por categoría:</p>

        {[
          { key: 'essential', label: '🔒 Cookies Esenciales', sub: 'Siempre activas — no se pueden desactivar', data: ESSENTIAL_COOKIES, color: 'green' },
          { key: 'analytics', label: '📊 Cookies Analíticas', sub: 'Requieren tu consentimiento', data: ANALYTICS_COOKIES, color: 'blue' },
          { key: 'preference', label: '⚙️ Cookies de Preferencia', sub: 'Requieren tu consentimiento', data: PREF_COOKIES, color: 'purple' },
          { key: 'marketing', label: '📣 Cookies de Marketing', sub: 'Requieren tu consentimiento', data: MARKETING_COOKIES, color: 'orange' },
        ].map(({ key, label, sub, data, color }) => (
          <div key={key} className="my-6 border border-gray-200 rounded-2xl overflow-hidden">
            <button
              onClick={() => toggle(key)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <div>
                <h3 className="font-bold text-gray-900">{label}</h3>
                <p className="text-sm text-gray-500 mt-0.5">{sub}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">{data.length} cookies</span>
                <span className="text-gray-400">{expanded[key] ? '▲' : '▼'}</span>
              </div>
            </button>
            {expanded[key] && <CookieTable cookies={data} />}
          </div>
        ))}
      </Section>

      <Section id="terceros" title="4. Cookies de Terceros">
        <p>Además de nuestras propias cookies, permitimos que determinados proveedores externos coloquen cookies en tu dispositivo. A continuación, los principales:</p>
        <InfoTable rows={[
          ['Google Analytics', 'Análisis estadístico del uso de la plataforma. Política de privacidad: policies.google.com/privacy'],
          ['Google Ads', 'Medición de conversiones de campañas publicitarias. Política: policies.google.com/privacy'],
          ['Meta (Facebook)', 'Pixel de Meta para campañas en redes sociales. Política: facebook.com/privacy/explanation'],
          ['Stripe', 'Procesamiento seguro de pagos. Política: stripe.com/privacy'],
          ['Mapbox / OpenStreetMap', 'Mapas interactivos para visualización de restaurantes. Política: mapbox.com/privacy'],
          ['Twilio / WhatsApp', 'Envío de notificaciones WhatsApp. Política: twilio.com/legal/privacy'],
        ]} />
        <p>OkReserva no controla las cookies colocadas por terceros. Te recomendamos revisar las políticas de privacidad de estos proveedores para obtener más información.</p>
      </Section>

      <Section id="gestion" title="5. Cómo Gestionar las Cookies">
        <p>Tienes varias opciones para gestionar las cookies:</p>

        <h3 className="text-base font-bold text-gray-900 mt-4 mb-2">Desde la Plataforma OkReserva</h3>
        <p>Puedes actualizar tus preferencias de cookies en cualquier momento desde <strong>Configuración → Privacidad y cookies</strong> en tu cuenta de OkReserva.</p>

        <h3 className="text-base font-bold text-gray-900 mt-4 mb-2">Desde tu Navegador</h3>
        <p>Puedes configurar tu navegador para rechazar o eliminar cookies. Instrucciones para los principales navegadores:</p>
        <InfoTable rows={[
          ['Google Chrome', 'Configuración → Privacidad y seguridad → Cookies'],
          ['Mozilla Firefox', 'Opciones → Privacidad y seguridad → Cookies y datos del sitio'],
          ['Safari', 'Preferencias → Privacidad → Gestionar datos de sitios web'],
          ['Microsoft Edge', 'Configuración → Privacidad, búsqueda y servicios → Cookies'],
          ['Opera', 'Configuración → Avanzado → Privacidad y seguridad → Cookies'],
        ]} />

        <AlertBox type="warning">
          Deshabilitar las cookies esenciales puede impedir que la plataforma funcione correctamente. Te recomendamos mantener activas al menos las cookies esenciales para una experiencia óptima.
        </AlertBox>

        <h3 className="text-base font-bold text-gray-900 mt-4 mb-2">Opt-out de Terceros</h3>
        <InfoTable rows={[
          ['Google Analytics', 'tools.google.com/dlpage/gaoptout'],
          ['Google Ads', 'adssettings.google.com'],
          ['Meta (Facebook)', 'facebook.com/settings?tab=ads'],
          ['Publicidad personalizada (DAA)', 'optout.aboutads.info'],
          ['Publicidad personalizada (NAI)', 'optout.networkadvertising.org'],
        ]} />
      </Section>

      <Section id="preferencias" title="6. Centro de Preferencias de Cookies">
        <p>Al acceder por primera vez a OkReserva, te presentamos un banner de consentimiento donde puedes:</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li><Highlight>Aceptar todas</Highlight> — Acepta cookies esenciales, analíticas, de preferencia y de marketing</li>
          <li><Highlight>Rechazar opcionales</Highlight> — Solo se activan las cookies esenciales</li>
          <li><Highlight>Personalizar</Highlight> — Elige qué categorías de cookies aceptas</li>
        </ul>
        <p>Tu elección queda registrada en la cookie <strong>okr_consent</strong> y tiene validez de 1 año. Puedes cambiar tus preferencias en cualquier momento desde el enlace "Configuración de cookies" en el pie de página.</p>
        <AlertBox type="success">
          OkReserva respeta el principio de privacidad por diseño: las cookies no esenciales están desactivadas por defecto hasta que otorgues tu consentimiento explícito.
        </AlertBox>
      </Section>

      <Section id="cambios" title="7. Cambios a esta Política de Cookies">
        <p>OkReserva puede actualizar esta Política de Cookies cuando sea necesario, por ejemplo, al implementar nuevas funcionalidades, integrar nuevos proveedores o adaptarla a cambios legislativos.</p>
        <p>Cuando realicemos cambios relevantes:</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Actualizaremos la fecha de "Última actualización" en esta página</li>
          <li>Te notificaremos mediante el banner de consentimiento</li>
          <li>Para cambios sustanciales, solicitaremos tu consentimiento nuevamente</li>
        </ul>
      </Section>

      <Section id="contacto" title="8. Contacto">
        <p>Si tienes preguntas sobre nuestra Política de Cookies o sobre el tratamiento de tus datos, contacta con nuestro equipo:</p>
        <InfoTable rows={[
          ['Empresa', co.legal_name || co.name],
          ['Correo de privacidad', co.dpa_email || co.email],
          ['Correo de soporte', co.support_email || co.email],
          ['Teléfono', co.phone || '—'],
          ['Sitio web', co.website],
        ]} />
      </Section>
    </LegalLayout>
  );
}
